import React, { useContext, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { HStack, Button, VStack, Text } from '@chakra-ui/react';
import Tippy from '@tippyjs/react';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';
import styled from '../../../../consts/styled';
import { media, spacing } from '../../../../App/ChakraTheme';

import ClassComponent from '../../components/ClassComponent';
import { PinningComponent } from '../../components/PinningComponent';
import { AuthContext, Role } from '../../../../context/AuthProvider';
import NavigationSecondRow from '../../components/NavigationSecondRow';
import { UnitPage } from '../Unit/UnitPage';
import { Page } from '../../../../types';
import secondsToTime from '../../../../helpers/secondsToTime';
import { fullNameUsernameOrEmail } from '../../../../helpers/username';
import Loader from '../../../../components/Loader';
import ClassPage from '../Class';
import { DASHBOARD_STUDYGROUPS_AND_PINNIGS } from '../queries';
import { Query } from '../../../../generated/graphqlModels';
import { ArrowRightIcon } from '@chakra-ui/icons';
import { trackAction } from '../../../../helpers/trackAction';
import { useTranslation } from 'react-i18next';

const LeftColumn = styled(VStack)`
  width: 30%;
  flex: 1 0 70%;
  transition: flex-basis 400ms;
  ${media.tablet`
    width: 100%;
    flex: 1 0 100%;
  `};
`;

const RightColumn = styled(VStack)`
  width: 30%;
  padding-left: 24px;
  flex: 0 1 70%;
  ${media.tablet`
    width: 100%;
    flex: 0 1 100%;
  `};
`;

const updateQuery = (previousResult: any, { fetchMoreResult }: any) => {
  if (!fetchMoreResult) {
    return previousResult;
  }

  const previousEdges = previousResult.studyGroups;
  const fetchMoreEdges = fetchMoreResult.studyGroups;

  fetchMoreResult.studyGroups = [...previousEdges, ...fetchMoreEdges];

  return {
    ...fetchMoreResult,
    pinnings: previousResult.pinnings,
  };
};

const LOAD_INCREMENT = 10;

const DashboardPageOld: Page = styled(({ className }) => {
  const user = useContext(AuthContext);
  const { t } = useTranslation();
  const [loadCount, setLoadCount] = useState<number>(LOAD_INCREMENT);

  const { data, loading, fetchMore } = useQuery<Query>(
    DASHBOARD_STUDYGROUPS_AND_PINNIGS,
    {
      variables: {
        userId: user?.pk,
        limit: loadCount,
        loadPinnings: true,
        customFilters: {
          isSelfLearning: false,
          isExplore: false,
          fieldsOrFilter: user?.flags.includes('only-related-classes')
            ? {
                fields: [
                  {
                    field: 'student_affiliations__user__id',
                    lookup: { iExact: user?.pk },
                  },
                  {
                    field: 'teacher_affiliations__user__id',
                    lookup: { iExact: user?.pk },
                  },
                ],
              }
            : undefined,
        },
      },
      // when signed out, user will be { is_authenticated: false }
      skip: user == null || user.pk == null,
    },
  );

  const [fetchingMore, setFetchingMore] = useState<boolean>(false);

  const hasMore =
    data && data.studyGroups && loadCount <= data.studyGroups.length;

  if (!user) {
    return null;
  }

  if (user.is_authenticated === false) {
    return <Navigate to="/sign-in/?next=/" />;
  }

  const isStaff = user?.roles.includes(Role.staff) || false;

  if (user.pending) {
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          marginTop: '50px',
          gap: '50px',
          textAlign: 'center',
        }}
      >
        <Text as="h3" textStyle="h3">
          {t('oldDashboardPage.accountPending')}
        </Text>

        <Text as="h5" textStyle="h5">
          {user.has_email
            ? t('oldDashboardPage.needToAcceptEmail')
            : t('oldDashboardPage.teacherNeedsToConfirm')}
        </Text>

        <Button
          colorScheme="blue"
          mt={8}
          onClick={() => window.location.reload()}
        >
          {t('general.refresh')}
        </Button>
      </div>
    );
  }

  const time_left = (user?.demo_end || 0) - new Date().getTime() / 1000;

  return (
    <>
      <NavigationSecondRow>
        <Text as="h5" textStyle="h5">
          {t('greeting.welcome')},{' '}
          {fullNameUsernameOrEmail(user) || 'My Friend'}
          {time_left > 0 ? ` (${secondsToTime(time_left)} demo time left)` : ''}
        </Text>
      </NavigationSecondRow>
      <div className={className}>
        <HStack w="full" className="row-cs" justify="start" align="start">
          <LeftColumn className="col-cs" align="start">
            <Text
              as="h4"
              textStyle="h4"
              style={{
                padding: '20px 0',
                borderBottom: '1px solid rgb(224, 219, 219)',
                marginBottom: '20px',
              }}
            >
              {user?.pages?.study_group
                ? t('oldDashboardPage.enrolledClasses')
                : t('oldDashboardPage.classes')}
            </Text>
            {loading && <Loader />}
            <VStack gap="2">
              {data &&
                data.studyGroups &&
                // @ts-ignore
                data.studyGroups?.map((studyGroup, index) => {
                  const expireDate = new Date(
                    studyGroup.license?.period?.lastDate,
                  );
                  expireDate.setHours(23, 59, 59);
                  const isExpired = expireDate < new Date();
                  return isExpired ? (
                    <Tippy
                      arrow={true}
                      theme="light"
                      animation="scale"
                      delay={250}
                      content={t('oldDashboardPage.licenseExpired')}
                    >
                      <div style={{ width: '100%' }}>
                        <ClassComponent
                          key={studyGroup.id}
                          to={ClassPage.getRoute(
                            studyGroup.license.program.id,
                            'units',
                            `?study-group-id=${studyGroup.id}`,
                          )}
                          {...studyGroup.license.program}
                          name={`${studyGroup.license.program.name} - ${studyGroup.name}`}
                          enabled={isStaff || false}
                          sameHeight={false}
                          showInfo={index === 0}
                        />
                      </div>
                    </Tippy>
                  ) : (
                    <ClassComponent
                      key={studyGroup.id}
                      to={ClassPage.getRoute(
                        studyGroup.license.program.id,
                        'units',
                        `?study-group-id=${studyGroup.id}`,
                      )}
                      {...studyGroup.license.program}
                      name={`${studyGroup.license.program.name} - ${studyGroup.name}`}
                      enabled={user['pages']['study_group']}
                      sameHeight={false}
                      showInfo={index === 0}
                    />
                  );
                })}
              {user.is_demo ? (
                <a href="https://marketplace.stemi.education/">
                  <Button
                    leftIcon={<ArrowRightIcon />}
                    colorScheme="blue"
                    variant="ghost"
                  >
                    {t('oldDashboardPage.allPrograms')}
                  </Button>
                </a>
              ) : null}
            </VStack>
            {hasMore && (
              <HStack mt="4" justify="center" w="full">
                <Button
                  size="sm"
                  colorScheme="blue"
                  isLoading={loading || fetchingMore}
                  onClick={() => {
                    setFetchingMore(true);
                    fetchMore({
                      variables: {
                        limit: LOAD_INCREMENT,
                        offset: loadCount,
                        loadPinnings: false,
                      },
                      updateQuery,
                    })
                      .then(() => {
                        setLoadCount(loadCount + LOAD_INCREMENT);
                      })
                      .finally(() => {
                        setFetchingMore(false);
                      });

                    trackAction({
                      action_type: 'button_click',
                      data: {
                        url: window.location.pathname,
                        text: t('button.loadMore'),
                      },
                    });
                  }}
                >
                  {t('button.loadMore')}
                </Button>
                <Button
                  size="sm"
                  colorScheme="blue"
                  isLoading={loading || fetchingMore}
                  onClick={() => {
                    setFetchingMore(true);
                    fetchMore({
                      variables: {
                        limit: 5000,
                        offset: loadCount,
                        loadPinnings: false,
                      },
                      updateQuery,
                    })
                      .then(() => {
                        setLoadCount(loadCount + 5000);
                      })
                      .finally(() => {
                        setFetchingMore(false);
                      });

                    trackAction({
                      action_type: 'button_click',
                      data: {
                        url: window.location.pathname,
                        text: 'Load all',
                      },
                    });
                  }}
                >
                  {t('button.loadAll')}
                </Button>
              </HStack>
            )}
          </LeftColumn>
          {user?.pages?.study_group && data && data.pinnings?.length > 0 && (
            <RightColumn className="col-cs" w="full" align="start">
              <Text as="h5" textStyle="h5" borderBottom="1px solid #b5b5b5">
                {t('oldDashboardPage.quickAccess')}
              </Text>
              {data.pinnings.map((pinning, index) => (
                <PinningComponent
                  user={user}
                  key={index}
                  route={UnitPage.content.getRoute(
                    pinning.studyGroup.program.id,
                    pinning.content.id,
                    `?study-group-id=${pinning.studyGroup.id}`,
                  )}
                  obj={pinning}
                />
              ))}
            </RightColumn>
          )}
        </HStack>
      </div>
    </>
  );
})`
  padding-left: ${spacing.BASE_PLUS};
  padding-right: ${spacing.BASE_PLUS};

  > h3.chakra-text {
    text-align: center;
  }

  > h1.chakra-text {
    text-align: center;
    padding-top: ${spacing.BASE_PLUS};
    padding-bottom: ${spacing.BASE_PLUS};
  }

  > .row-cs.chakra-stack {
    margin-right: -${spacing.TINY};
    margin-left: -${spacing.TINY};

    ${media.tablet`
    flex-wrap: wrap-reverse;
  `}
    > .col-cs.chakra-stack {
      padding-right: ${spacing.TINY};
      padding-left: ${spacing.TINY};
      padding-bottom: ${spacing.LARGE};

      > h3.chakra-text {
        padding-bottom: 10px;
      }
    }
  }
` as any;

DashboardPageOld.getRoute = () => '/';

export default DashboardPageOld;
